import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

// Premium grid: first photo large, then an even grid; tap opens a full-screen swipeable lightbox.
export const ListingGallery = ({ photos, address }) => {
  const [index, setIndex] = useState(null);
  const touchX = useRef(null);
  const count = photos.length;
  const close = useCallback(() => setIndex(null), []);
  const step = useCallback((d) => setIndex((i) => (i + d + count) % count), [count]);

  useEffect(() => {
    if (index === null) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [index, close, step]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="listing-gallery">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C6A14A]">Gallery</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mt-2">Inside &amp; out</h2>
        </div>
        <p className="text-sm text-[#E8E4DE]/50" data-testid="listing-photo-count">{count} photos</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {photos.map((p, i) => (
          <button key={p.id} type="button" onClick={() => setIndex(i)} data-testid={`listing-photo-${i}`}
            aria-label={`Open photo ${i + 1} of ${count}`}
            className={`group relative overflow-hidden rounded-lg bg-[#0f1724] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A14A] ${i === 0 ? 'col-span-2 row-span-2 aspect-[4/3]' : 'aspect-[4/3]'}`}>
            <img src={p.url} alt={`${address} — photo ${i + 1}`} loading={i < 5 ? 'eager' : 'lazy'}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
            <span className="absolute inset-0 bg-[#050816]/0 group-hover:bg-[#050816]/20 transition-colors duration-300" />
          </button>
        ))}
      </div>

      {index !== null && (
        <div className="fixed inset-0 z-[1100] bg-black/95 flex items-center justify-center" role="dialog" aria-modal="true" aria-label="Photo viewer"
          data-testid="listing-lightbox" onClick={close}
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
            if (Math.abs(dx) > 45) step(dx < 0 ? 1 : -1);
          }}>
          <span className="absolute top-4 left-4 text-sm text-white/70 bg-white/10 px-4 py-2 rounded-full" data-testid="listing-lightbox-counter">{index + 1} / {count}</span>
          <button type="button" onClick={close} aria-label="Close" data-testid="listing-lightbox-close"
            className="absolute top-3 right-3 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous photo" data-testid="listing-lightbox-prev"
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-[#C6A14A] hover:text-[#050816] text-white items-center justify-center transition-colors"><ChevronLeft className="h-6 w-6" /></button>
          <img key={index} src={photos[index].url} alt={`${address} — photo ${index + 1}`} onClick={(e) => e.stopPropagation()}
            className="max-h-[86vh] max-w-[96vw] object-contain rounded animate-in fade-in duration-300" />
          <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next photo" data-testid="listing-lightbox-next"
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-[#C6A14A] hover:text-[#050816] text-white items-center justify-center transition-colors"><ChevronRight className="h-6 w-6" /></button>
          <p className="absolute bottom-5 inset-x-0 text-center text-xs text-white/40 sm:hidden">Swipe to see more</p>
        </div>
      )}
    </section>
  );
};
